import PropTypes from 'prop-types';
import formatDateToDDMMYYYY from '../../utils/formatDateToDDMMYYYY';
import PayJimLogo from '../../assets/svg/payjim-logo.svg';
import Button from '../common/Button';
import EditIcon from '../../assets/dynamic-color-svgs/EditIcon';

const SingleInvoice = ({
  folioId,
  invoiceDate,
  total,
  taxDetails,
  guestData,
  lineItems,
  setEditInvoiceAddressMode,
}) => {
  const invoiceDateStr = formatDateToDDMMYYYY(new Date(invoiceDate));
  let subtotal = 0;
  let vat = 0;

  if (Array.isArray(taxDetails) && taxDetails.length > 0) {
    subtotal = taxDetails.reduce(
      (sum, taxItem) => sum + (taxItem.net?.amount || 0),
      0,
    );
    vat = taxDetails.reduce(
      (sum, taxItem) => sum + (taxItem.tax?.amount || 0),
      0,
    );
  }

  return (
    <div className="flex w-full flex-col gap-6 rounded-xl border-2 border-gray-200 bg-white p-3 xxs:p-4 xs:p-6">
      {/* Logo */}
      <div className="flex justify-end">
        <img className="w-16" src={PayJimLogo} alt="PayJim Logo" />
      </div>
      {/* Guest's Information */}
      <div className="flex flex-col">
        <h5 className="font-semibold underline">Billed to</h5>
        <span>{guestData.name}</span>
        <span>{guestData.address.addressLine1}</span>
        <span>{`${guestData.address.postalCode} ${guestData.address.city}, ${guestData.address.countryCode}`}</span>
        <div className="mt-3 max-w-max">
          <Button
            variant="transparent"
            text="Edit invoice address"
            onClick={() => setEditInvoiceAddressMode(true)}
            zeroPadding
            semibold
            small
            leftIcon={<EditIcon />}
          />
        </div>
      </div>
      {/* Invoice Details */}
      <div className="ml-auto mt-2 flex max-w-max flex-col">
        <div className="flex gap-2">
          <span className="font-semibold">Invoice date:</span>
          <span>{invoiceDateStr}</span>
        </div>
        <div className="flex gap-2">
          <span className="font-semibold">Folio Id:</span>
          <span>{folioId}</span>
        </div>
      </div>
      <div>
        <span className="font-bold">{`Dear ${guestData.name},`}</span>
        <p>
          Thank you for your payment. Please find a preview of your invoice
          below. You may email the invoice or download it as PDF.
        </p>
      </div>

      {/* Table */}
      <table className="border-separate border-spacing-y-2">
        <thead>
          <tr>
            <th className="border-b border-pj-gray-800 text-left">Pos</th>
            <th className="border-b border-pj-gray-800 text-left">
              Description
            </th>
            <th className="border-b border-pj-gray-800 text-right">Price(€)</th>
          </tr>
        </thead>
        <tbody className="mt-4">
          {lineItems.map(
            (item, index) => (
              <tr
                key={item.id || index}
                className="border-b border-pj-gray-500 align-top"
              >
                <td>{index + 1}</td>
                <td>{item.description}</td>
                <td className="text-right">
                  {`${item.price.amount.toFixed(
                    2,
                  )} ${item.price.currency}`}
                </td>
              </tr>
            ),
          )}
        </tbody>
      </table>
      {/* Invoice Summary */}
      <div className="ml-auto flex w-3/5 max-w-64 flex-col border-t border-pj-gray-800 py-4">
        <div className="flex justify-between gap-2">
          <span>Subtotal:</span>
          <span className="text-right font-semibold">
            {`${subtotal.toFixed(2)} ${total.currency}`}
          </span>
        </div>
        <div className="flex justify-between gap-2">
          <span>VAT:</span>
          <span className="text-right font-semibold">
            {`${vat.toFixed(2)} ${
              total.currency
            }`}
          </span>
        </div>
        <div className="flex justify-between gap-2 font-semibold text-pj-primary-400">
          <span>Total:</span>
          <span className="pt-2 text-right text-lg font-semibold">
            {`${
              total ? total.amount.toFixed(2) : 0
            } ${total ? total.currency : 'EUR'}`}
          </span>
        </div>
      </div>
      <div>
        <div>Best regards,</div>
        <div className="font-semibold">Your PayJim - Team</div>
      </div>
    </div>
  );
};

SingleInvoice.propTypes = {
  folioId: PropTypes.string.isRequired,
  invoiceDate: PropTypes.string.isRequired,
  total: PropTypes.shape({
    price: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
  }),
  guestData: PropTypes.shape({
    title: PropTypes.string.isRequired,
    firstName: PropTypes.string.isRequired,
    lastName: PropTypes.string.isRequired,
    street: PropTypes.string.isRequired,
    zipCode: PropTypes.string.isRequired,
    city: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
  }),
  taxDetails: PropTypes.arrayOf(
    PropTypes.shape({
      net: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
      }),
      tax: PropTypes.shape({
        amount: PropTypes.number.isRequired,
        currency: PropTypes.string.isRequired,
      }),
      vatPercent: PropTypes.number.isRequired,
      vatType: PropTypes.string.isRequired,
    }),
  ),
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      amount: PropTypes.number.isRequired,
    }),
  ).isRequired,
};

export default SingleInvoice;
